// Card individual para Web Vital com tooltip explicativo em PT-BR

import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  WEB_VITALS_CONFIG, 
  STATUS_CONFIG, 
  getMetricStatus, 
  formatMetricValue,
  type WebVitalKey,
} from '@/types/performance';

interface WebVitalCardProps {
  metricKey: WebVitalKey;
  value: number | null;
  score?: number;
  isLoading?: boolean;
}

export function WebVitalCard({ metricKey, value, isLoading }: WebVitalCardProps) {
  const config = WEB_VITALS_CONFIG[metricKey];
  const status = getMetricStatus(metricKey, value);
  const statusConfig = STATUS_CONFIG[status];

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-4 rounded-full" />
          </div>
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-3 w-24" />
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className={`relative overflow-hidden border ${statusConfig.borderColor} ${statusConfig.bgColor}`}>
        <CardContent className="p-4">
          {/* Header with metric name and info icon */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm">{config.name}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] p-3">
                  <p className="font-medium mb-1">{config.fullName}</p>
                  <p className="text-xs text-muted-foreground">{config.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <span className="text-lg">{statusConfig.icon}</span>
          </div>

          {/* Value */}
          <div className={`text-2xl font-bold ${statusConfig.textColor}`}>
            {formatMetricValue(metricKey, value)}
          </div>

          {/* Status label and full name */}
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-muted-foreground truncate">
              {config.fullName}
            </span>
            <span className={`text-xs font-medium ${statusConfig.textColor}`}>
              {statusConfig.label}
            </span>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
